import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft } from "lucide-react";
import OtterIcon from "@/components/app/OtterIcon";

export default function Register() {
  const { register, error } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await register(name, email, password);
    setBusy(false);
    if (ok) navigate("/app");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="p-8 lg:p-16 grid place-items-center bg-background order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground" data-testid="register-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back home
          </Link>
          <h1 className="font-semibold tracking-tight text-4xl mt-6">Create account.</h1>
          <div className="mt-4 inline-flex items-center gap-2 border border-foreground bg-foreground text-background px-3 py-1.5 text-xs font-mono-data uppercase tracking-widest" data-testid="register-trial-pill">
            ✦ 7-day Pro trial · no card required
          </div>
          <p className="text-sm text-muted-foreground mt-3">90 seconds. Magic Mode, AI sections, multi-platform social, auto-schedule — all unlocked for a week. Cancel anytime.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="name" className="font-mono-data text-xs uppercase tracking-widest">Name</Label>
              <Input id="name" required value={name} onChange={(e)=>setName(e.target.value)}
                className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground"
                data-testid="register-name-input" placeholder="Ada Lovelace" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="font-mono-data text-xs uppercase tracking-widest">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground"
                data-testid="register-email-input" placeholder="you@company.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="font-mono-data text-xs uppercase tracking-widest">Password</Label>
              <Input id="password" type="password" required minLength={6} value={password} onChange={(e)=>setPassword(e.target.value)}
                className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground"
                data-testid="register-password-input" placeholder="at least 6 chars" />
            </div>
            {error && (
              <div className="border border-destructive/40 bg-destructive/5 text-destructive text-sm px-3 py-2" data-testid="register-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={busy} className="w-full rounded-none h-11 bg-foreground hover:bg-foreground/90" data-testid="register-submit-button">
              {busy ? "Creating…" : "Create account →"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            Already a user?{" "}
            <Link to="/login" className="underline underline-offset-4 text-foreground" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative border-l border-border bg-primary text-primary-foreground order-1 lg:order-2">
        <div className="absolute inset-0 grid-bg opacity-15" />
        <div className="relative h-full p-12 flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="register-brand-link">
            <div className="w-7 h-7 bg-primary-foreground grid place-items-center text-primary">
              <OtterIcon size={18} />
            </div>
            <span className="font-semibold tracking-tight text-lg">ArloSEO</span>
          </Link>
          <div>
            <p className="font-mono-data text-xs uppercase tracking-widest text-primary-foreground/70">// included free</p>
            <ul className="mt-6 space-y-3 font-display tracking-tight text-2xl">
              <li>· 1 project</li>
              <li>· 20 tracked keywords</li>
              <li>· On-page audits</li>
              <li>· AI recommendations</li>
            </ul>
          </div>
          <div className="font-mono-data text-xs text-primary-foreground/60">NO_CARD · NO_LOCK_IN</div>
        </div>
      </div>
    </div>
  );
}
